import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import * as React from 'react';
import { Checkbox } from 'semantic-ui-react';
import { formatCurrency, getDefaultCurrencySymbol } from 'Util/_HumanWritten/CurrencyUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import {
	BookingCancellationInformationDto,
	canCancelMigratedBookingWithNoLimit,
	computeSelectedTotalForMethod,
	getMethodBookingCutoff,
	getTravelCreditTerminology,
	handleRefundChange,
	ModalConfig,
	refundMethodChoiceAvailable,
	RefundModel,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';
import { CardInformation } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/RefundBreakdown';
import { RefundMethodSelector } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/RefundMethodSelector';
import { BookingRefundList } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/SelectBookingsContent';
import If from 'Views/Components/If/If';
import { NumberTextField } from 'Views/Components/NumberTextBox/NumberTextBox';

export interface StaffModalProps {
	refundModel: RefundModel;
	cancellationInformation: BookingCancellationInformationDto;
	extraInfo: JSX.Element;
	canGiveRefund: boolean;
	canOverridePrice: boolean;
}

const getConfirmButtonText = (canGiveRefund: boolean) => {
	if (!canGiveRefund) {
		return 'Cancel booking';
	}
	const allowed = appConfigStore.allowedRefundMethodsFor(store.isStaff);
	if (allowed.has('Credit') && !allowed.has('Bank')) {
		return 'Send voucher';
	}
	return 'Confirm refund';
};

export const buildStaffModal = (
	cancellationInformation: BookingCancellationInformationDto,
	refundModel: RefundModel,
	canOverridePrice: boolean,
): ModalConfig => {
	const { invoiced, discounted, isUnpaid, maximumRefund } = cancellationInformation;

	const canGiveRefund = maximumRefund > 0 && !invoiced;
	const refundTerm = getTravelCreditTerminology();
	let extraInfo = <></>;

	// Handle ineligible cases
	if (invoiced) {
		extraInfo = <p>This booking is not eligible for a {refundTerm} as it was invoiced.</p>;
	}

	if (discounted && !canGiveRefund) {
		extraInfo = <p>This booking is not eligible for a {refundTerm} as it was fully discounted.</p>;
	}

	if (isUnpaid && !canGiveRefund) {
		extraInfo = <p>This booking is not eligible for a {refundTerm} as it is unpaid.</p>;
	} else if (isUnpaid && canGiveRefund) {
		extraInfo = (
			<p>
				<strong>Note: </strong>Any unpaid amounts are not eligible for a {refundTerm}.
			</p>
		);
	}

	// Standard manager modal
	const ObservableContent = observer(() => (
		<StaffModalContent
			refundModel={refundModel}
			cancellationInformation={cancellationInformation}
			extraInfo={extraInfo}
			canGiveRefund={canGiveRefund}
			canOverridePrice={canOverridePrice}
		/>
	));

	return {
		title: 'Cancel booking',
		content: <ObservableContent />,
		cancelButtonText: 'Close',
		confirmButtonText: getConfirmButtonText(canGiveRefund),
		disableConfirmButtonFunc: () => stringNotEmpty(refundModel.refundAmountErrors),
	};
};

export const StaffModalContent = observer(
	({ refundModel, cancellationInformation, extraInfo, canGiveRefund, canOverridePrice }: StaffModalProps) => {
		const { bookings, cardsToRefund, invoiced, isUnpaid } = cancellationInformation;

		const selectedMaximumRefund = bookings
			.filter(booking => refundModel.selectedBookings.includes(booking.id))
			.reduce((sum, booking) => sum + booking.maximumRefund, 0);

		const selectedRecommendedRefund = computeSelectedTotalForMethod(
			bookings,
			refundModel.selectedBookings,
			refundModel.forceCreditRefund,
		);

		const showMethodSelector = refundMethodChoiceAvailable() && !invoiced;
		const hasMatchedCutoff = bookings.some(b => getMethodBookingCutoff(b, refundModel.forceCreditRefund) != null);

		return (
			<div className="cancel-booking-modal-content">
				<BookingRefundList
					bookings={bookings}
					refundModel={refundModel}
					onRefundChange={(bookingId, amount) => handleRefundChange(bookingId, amount, refundModel)}
				/>

				<If condition={showMethodSelector}>
					<RefundMethodSelector bookings={bookings} refundModel={refundModel} />
				</If>

				{/* Card Refunds */}
				<If
					condition={
						appConfigStore.allowedRefundMethodsFor(store.isStaff).has('Bank') &&
						!refundModel.forceCreditRefund
					}
				>
					<CardInformation
						cardsToRefund={cardsToRefund}
						refundAmount={refundModel.refundAmount}
						forceFerryCreditRefund={refundModel.forceCreditRefund}
					/>
				</If>

				{extraInfo && extraInfo}

				<If condition={canOverridePrice}>
					<div>
						{hasMatchedCutoff && (
							<p>
								<strong>Recommended:</strong> {formatCurrency(selectedRecommendedRefund)} |{' '}
								<strong>Maximum:</strong> {formatCurrency(selectedMaximumRefund)}
							</p>
						)}
					</div>

					<If condition={canGiveRefund}>
						<div>
							<p className="input-label">
								{refundModel.forceCreditRefund ? 'Credit voucher' : 'Refund amount'}
							</p>

							<div className="custom-number-input-field new-style">
								<div className="dollar-icon">{getDefaultCurrencySymbol()}</div>
								<NumberTextField
									className="price-refund-textfield"
									id="cancellation-refund-textfield"
									model={refundModel}
									modelProperty="refundAmount"
									placeholder={selectedRecommendedRefund.toFixed(2)}
									onAfterChange={action(() => {
										validateRefundAmount(refundModel, selectedMaximumRefund, '');
									})}
									showErrors={false}
								/>
							</div>
							<If condition={stringNotEmpty(refundModel.refundAmountErrors)}>
								<p className="error-message">{refundModel.refundAmountErrors}</p>
							</If>
						</div>
					</If>
				</If>
			</div>
		);
	},
);

export const validateRefundAmount = (
	refundModel: RefundModel,
	maximumRefund: number,
	bookingLegacyId: any,
): boolean => {
	const canCancelMigrated = canCancelMigratedBookingWithNoLimit(bookingLegacyId);

	let isValid = false; // Variable to capture the result

	runInAction(() => {
		if (isNullOrUndefined(refundModel.refundAmount)) {
			isValid = false;
			refundModel.refundAmountErrors = `Please enter a number between $0 and ${formatCurrency(maximumRefund)}`;
		}
		if (canCancelMigrated) {
			if (refundModel.refundAmount < 0) {
				refundModel.refundAmountErrors = 'Must be greater than zero';
				isValid = false;
			} else {
				refundModel.refundAmountErrors = '';
				isValid = true; // No limit to credit for migrated bookings
			}
		} else if (refundModel.refundAmount >= 0 && refundModel.refundAmount <= maximumRefund) {
			refundModel.refundAmountErrors = '';
			isValid = true;
		} else {
			refundModel.refundAmountErrors = `Please enter a number between $0 and ${formatCurrency(maximumRefund)}`;
			isValid = false;
		}
	});

	return isValid;
};
